import React, { useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useAnimations } from '../contexts/AnimationContext'

interface FigmaStarfieldProps {
  className?: string
}

const FigmaStarfield: React.FC<FigmaStarfieldProps> = ({ className = '' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>()
  const { settings } = useAnimations()

  useEffect(() => {
    if (!settings.enabled || settings.reduceMotion) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let isDestroyed = false

    // Enhanced canvas setup matching Figma specs
    const resizeCanvas = () => {
      if (isDestroyed) return
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }

    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Figma-specific star types and properties
    const stars: Array<{
      x: number
      y: number
      z: number
      size: number
      speed: number
      color: string
      alpha: number
      twinkle: number
      twinkleSpeed: number
      type: 'micro' | 'small' | 'medium' | 'large' | 'nebula'
      pulsePhase: number
      pulseSpeed: number
      trail: { x: number; y: number; alpha: number }[]
    }> = []

    // Figma design colors (exact matches)
    const figmaColors = [
      '#ffffff', // Pure white
      '#a855f7', // Purple-500
      '#3b82f6', // Blue-500
      '#06b6d4', // Cyan-500
      '#10b981', // Emerald-500
      '#f59e0b', // Amber-500
      '#ef4444', // Red-500
      '#8b5cf6', // Violet-500
      '#14b8a6', // Teal-500
      '#f97316', // Orange-500
    ]

    // Snow colors (whites and light blues)
    const snowColors = [
      '#ffffff', // Pure white
      '#f8fafc', // Very light gray
      '#e2e8f0', // Light gray
      '#cbd5e1', // Slightly darker gray
      '#e0f2fe', // Very light cyan
      '#bae6fd', // Light cyan
      '#dbeafe', // Very light blue
      '#bfdbfe', // Light blue
    ]

    // Generate stars with Figma design distribution
    const totalStars = settings.particleCount
    for (let i = 0; i < totalStars; i++) {
      const rand = Math.random()
      let type: 'micro' | 'small' | 'medium' | 'large' | 'nebula'
      
      // Figma star distribution
      if (rand < 0.4) type = 'micro'
      else if (rand < 0.7) type = 'small'
      else if (rand < 0.9) type = 'medium'
      else if (rand < 0.98) type = 'large'
      else type = 'nebula'

      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        z: Math.random() * 2000 + 100,
        size: type === 'micro' ? 0.5 : 
              type === 'small' ? 1 :
              type === 'medium' ? 2 :
              type === 'large' ? 3 : 5,
        speed: (Math.random() * 2 + 0.5) * settings.starfieldSpeed,
        color: settings.preset === 'snow' 
          ? snowColors[Math.floor(Math.random() * snowColors.length)]
          : figmaColors[Math.floor(Math.random() * figmaColors.length)],
        alpha: Math.random() * 0.8 + 0.2,
        twinkle: Math.random() * Math.PI * 2,
        twinkleSpeed: Math.random() * 0.02 + 0.01,
        type,
        pulsePhase: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.03 + 0.01,
        trail: []
      })
    }

    // Nebula particles for depth
    const nebulae: Array<{
      x: number
      y: number
      size: number
      color: string
      alpha: number
      drift: { x: number; y: number }
    }> = []

    for (let i = 0; i < 20; i++) {
      nebulae.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 200 + 100,
        color: figmaColors[Math.floor(Math.random() * figmaColors.length)],
        alpha: Math.random() * 0.03 + 0.01,
        drift: {
          x: (Math.random() - 0.5) * 0.1,
          y: (Math.random() - 0.5) * 0.1
        }
      })
    }

    let time = 0

    const animate = () => {
      if (isDestroyed) return

      time += 0.016 // ~60fps

      // Figma background gradient
      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, Math.max(canvas.width, canvas.height)
      )
      gradient.addColorStop(0, 'rgba(26, 26, 46, 1)') // #1a1a2e
      gradient.addColorStop(0.5, 'rgba(16, 20, 31, 1)') // #10141f
      gradient.addColorStop(1, 'rgba(10, 14, 26, 1)') // #0a0e1a

      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Render nebulae first (background layer)
      if (settings.backgroundAnimations) {
        nebulae.forEach(nebula => {
          nebula.x += nebula.drift.x
          nebula.y += nebula.drift.y
          
          // Wrap around screen
          if (nebula.x < -nebula.size) nebula.x = canvas.width + nebula.size
          if (nebula.x > canvas.width + nebula.size) nebula.x = -nebula.size
          if (nebula.y < -nebula.size) nebula.y = canvas.height + nebula.size
          if (nebula.y > canvas.height + nebula.size) nebula.y = -nebula.size

          const nebulaGradient = ctx.createRadialGradient(
            nebula.x, nebula.y, 0,
            nebula.x, nebula.y, nebula.size
          )
          nebulaGradient.addColorStop(0, nebula.color + Math.floor(nebula.alpha * 255).toString(16).padStart(2, '0'))
          nebulaGradient.addColorStop(0.3, nebula.color + '08')
          nebulaGradient.addColorStop(1, 'transparent')

          ctx.fillStyle = nebulaGradient
          ctx.fillRect(nebula.x - nebula.size, nebula.y - nebula.size, nebula.size * 2, nebula.size * 2)
        })
      }

      // Render stars with Figma-perfect animation
      const centerX = canvas.width / 2
      const centerY = canvas.height / 2

      stars.forEach((star) => {
        // Snow mode: vertical falling animation
        if (settings.preset === 'snow') {
          star.y += star.speed * 2 // Fall downward
          star.x += Math.sin(star.twinkle) * 0.5 // Gentle swaying
          
          // Reset particle when it falls off screen
          if (star.y > canvas.height + 10) {
            star.x = Math.random() * canvas.width
            star.y = -10
            star.z = Math.random() * 500 + 100 // Closer z for snow effect
          }

          // Simple 2D projection for snow
          const scale = Math.max(0.3, 500 / star.z)
          const x = star.x
          const y = star.y
        } else {
          // Original starfield: Update position with perspective
          star.z -= star.speed
          if (star.z <= 0) {
            star.x = Math.random() * canvas.width
            star.y = Math.random() * canvas.height
            star.z = 2000
            star.trail = []
          }
        }

        // 3D projection (for non-snow modes)
        const scale = settings.preset === 'snow' ? Math.max(0.3, 500 / star.z) : 1000 / star.z
        const x = settings.preset === 'snow' ? star.x : (star.x - centerX) * scale + centerX
        const y = settings.preset === 'snow' ? star.y : (star.y - centerY) * scale + centerY

        // Update animation phases
        star.twinkle += star.twinkleSpeed
        star.pulsePhase += star.pulseSpeed

        // Calculate size and opacity based on Figma specs
        const distance = (2000 - star.z) / 2000
        const twinkleIntensity = (Math.sin(star.twinkle) + 1) / 2
        const pulseIntensity = (Math.sin(star.pulsePhase) + 1) / 2
        
        let currentSize = star.size * scale * (0.5 + distance * 0.5)
        if (star.type === 'large' || star.type === 'nebula') {
          currentSize *= (0.8 + twinkleIntensity * 0.4)
        }

        const opacity = Math.min(star.alpha * distance * (0.6 + twinkleIntensity * 0.4), 1)

        // Trail effect for fast-moving stars
        if (distance > 0.3 && star.trail.length > 0) {
          ctx.globalCompositeOperation = 'screen'
          for (let i = 0; i < star.trail.length; i++) {
            const trailPoint = star.trail[i]
            ctx.fillStyle = star.color + Math.floor(trailPoint.alpha * 255).toString(16).padStart(2, '0')
            ctx.beginPath()
            ctx.arc(trailPoint.x, trailPoint.y, currentSize * (i / star.trail.length), 0, Math.PI * 2)
            ctx.fill()
          }
          ctx.globalCompositeOperation = 'source-over'
        }

        // Update trail
        if (star.trail.length > 5) star.trail.shift()
        star.trail.push({ x, y, alpha: opacity * 0.3 })

        if (x >= -100 && x <= canvas.width + 100 && y >= -100 && y <= canvas.height + 100) {
          // Glow effect for special stars
          if (settings.glowEffects && (star.type === 'large' || star.type === 'nebula')) {
            const glowSize = currentSize * (star.type === 'nebula' ? 8 : 4)
            const glowGradient = ctx.createRadialGradient(x, y, 0, x, y, glowSize)
            glowGradient.addColorStop(0, star.color + '80')
            glowGradient.addColorStop(0.3, star.color + '40')
            glowGradient.addColorStop(0.7, star.color + '10')
            glowGradient.addColorStop(1, 'transparent')

            ctx.fillStyle = glowGradient
            ctx.fillRect(x - glowSize, y - glowSize, glowSize * 2, glowSize * 2)
          }

          // Main star body
          ctx.fillStyle = star.color
          ctx.globalAlpha = opacity
          ctx.beginPath()
          ctx.arc(x, y, Math.max(currentSize, 0.3), 0, Math.PI * 2)
          ctx.fill()

          // Sparkle cross for large stars (Figma-style)
          if ((star.type === 'large' || star.type === 'nebula') && twinkleIntensity > 0.7) {
            ctx.strokeStyle = star.color
            ctx.globalAlpha = opacity * 0.9
            ctx.lineWidth = star.type === 'nebula' ? 2 : 1
            const sparkleSize = currentSize * (star.type === 'nebula' ? 4 : 3)
            
            ctx.beginPath()
            ctx.moveTo(x - sparkleSize, y)
            ctx.lineTo(x + sparkleSize, y)
            ctx.moveTo(x, y - sparkleSize)
            ctx.lineTo(x, y + sparkleSize)
            ctx.stroke()
          }

          // Pulse ring for nebula stars
          if (star.type === 'nebula' && pulseIntensity > 0.8) {
            ctx.strokeStyle = star.color
            ctx.globalAlpha = opacity * 0.5
            ctx.lineWidth = 1
            const ringSize = currentSize * (2 + pulseIntensity * 2)
            ctx.beginPath()
            ctx.arc(x, y, ringSize, 0, Math.PI * 2)
            ctx.stroke()
          }
        }

        ctx.globalAlpha = 1
      })

      if (!isDestroyed) {
        animationRef.current = requestAnimationFrame(animate)
      }
    }

    animate()

    return () => {
      isDestroyed = true
      window.removeEventListener('resize', resizeCanvas)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [settings])

  if (!settings.enabled || settings.reduceMotion) {
    return (
      <div 
        className={`fixed inset-0 pointer-events-none z-0 ${className}`}
        style={{ background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #0a0e1a 100%)' }}
      />
    )
  }

  return (
    <motion.canvas
      ref={canvasRef}
      className={`fixed inset-0 pointer-events-none z-0 ${className}`}
      style={{ background: 'transparent' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    />
  )
}

export default FigmaStarfield